import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, ToggleLeft, ToggleRight, Tag, Loader2 } from 'lucide-react';

export function AdminCouponsTab({ coupons, newCoupon, setNewCoupon, isCreatingCoupon, handleCreateCoupon, handleDeleteCoupon, handleToggleCoupon }) {
  return (
    <div className="space-y-6">
      {/* Create Coupon Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Create New Coupon
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateCoupon} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Code *</Label>
              <Input
                placeholder="e.g. WELCOME20"
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                className="uppercase tracking-wider font-mono"
                required
                data-testid="coupon-code-input"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Type *</Label>
              <Select value={newCoupon.type} onValueChange={(v) => setNewCoupon({ ...newCoupon, type: v })}>
                <SelectTrigger data-testid="coupon-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="amount">Fixed Amount (INR)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-medium">
                Value * {newCoupon.type === 'percentage' ? '(%)' : '(INR)'}
              </Label>
              <Input
                type="number"
                placeholder={newCoupon.type === 'percentage' ? 'e.g. 20' : 'e.g. 200'}
                value={newCoupon.value}
                onChange={(e) => setNewCoupon({ ...newCoupon, value: e.target.value })}
                min="0"
                required
                data-testid="coupon-value-input"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Expiry Date *</Label>
              <Input
                type="date"
                value={newCoupon.expiry_date}
                onChange={(e) => setNewCoupon({ ...newCoupon, expiry_date: e.target.value })}
                required
                data-testid="coupon-expiry-input"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Min Order Value (INR)</Label>
              <Input
                type="number"
                placeholder="0 = no minimum"
                value={newCoupon.min_order_value}
                onChange={(e) => setNewCoupon({ ...newCoupon, min_order_value: e.target.value })}
                min="0"
                data-testid="coupon-min-order-input"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Usage Limit</Label>
              <Input
                type="number"
                placeholder="0 = unlimited"
                value={newCoupon.usage_limit}
                onChange={(e) => setNewCoupon({ ...newCoupon, usage_limit: e.target.value })}
                min="0"
                data-testid="coupon-usage-limit-input"
              />
            </div>
            <div>
              <Label className="mb-1.5 block text-sm font-medium">Per User Limit</Label>
              <Input
                type="number"
                placeholder="1"
                value={newCoupon.per_user_limit}
                onChange={(e) => setNewCoupon({ ...newCoupon, per_user_limit: e.target.value })}
                min="0"
                data-testid="coupon-per-user-limit-input"
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-3 flex justify-end pt-2">
              <Button
                type="submit"
                disabled={isCreatingCoupon}
                style={{ background: 'linear-gradient(to right, #7A00EB, #00c3ef)' }}
                className="text-white hover:opacity-90 px-8"
                data-testid="create-coupon-btn"
              >
                {isCreatingCoupon ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Coupon
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Coupons List */}
      <Card>
        <CardHeader><CardTitle>All Coupons</CardTitle></CardHeader>
        <CardContent>
          {coupons.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Tag className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No coupons created yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Expiry</TableHead>
                    <TableHead>Min Order</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Per User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {coupons.map((coupon) => {
                    const isExpired = new Date(coupon.expiry_date) < new Date();
                    return (
                      <TableRow key={coupon.code} className={isExpired ? 'opacity-50' : ''}>
                        <TableCell className="font-mono font-bold text-sm">{coupon.code}</TableCell>
                        <TableCell className="text-sm">{coupon.type === 'percentage' ? 'Percentage' : 'Fixed'}</TableCell>
                        <TableCell className="font-semibold text-sm">
                          {coupon.type === 'percentage' ? `${coupon.value}%` : `₹${coupon.value}`}
                        </TableCell>
                        <TableCell className="text-sm">
                          <span className={isExpired ? 'text-red-500' : ''}>
                            {new Date(coupon.expiry_date).toLocaleDateString()}
                            {isExpired && ' (Expired)'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{coupon.min_order_value ? `₹${coupon.min_order_value / 100}` : '-'}</TableCell>
                        <TableCell className="text-sm">
                          {coupon.used_count || 0}{coupon.usage_limit ? ` / ${coupon.usage_limit}` : ' / ∞'}
                        </TableCell>
                        <TableCell className="text-sm">{coupon.per_user_limit || '∞'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${coupon.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {coupon.active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => handleToggleCoupon(coupon.code)} title={coupon.active ? 'Deactivate' : 'Activate'} data-testid={`toggle-coupon-${coupon.code}`}>
                              {coupon.active ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4 text-gray-400" />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteCoupon(coupon.code)} className="text-red-500 hover:text-red-700" data-testid={`delete-coupon-${coupon.code}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
